import type React from 'react';
import { forwardRef, useId } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './text-field.module.scss';

export interface WordsHappenTextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label, tied to the input. */
  label: React.ReactNode;
  /** Help text below the input; stays visible alongside an error. */
  hint?: React.ReactNode;
  /** Error message. When set the input is invalid and the message is announced. */
  error?: React.ReactNode;
  /** Content inside the field's box, after the input, e.g. a show/hide button. */
  rightSlot?: React.ReactNode;
}

const hasContent = (node: React.ReactNode) => node !== undefined && node !== null && node !== false && node !== '';

/**
 * A labelled single-line input. The label, hint and error are wired up with `for`, `aria-describedby` and
 * `aria-invalid`; the error sits in a polite live region so it is read out when it appears.
 */
export const WordsHappenTextField = forwardRef<HTMLInputElement, WordsHappenTextFieldProps>(
  ({ label, hint, error, rightSlot, id, className, disabled, 'aria-describedby': describedBy, ...rest }, ref) => {
    const { theme } = useWordsHappenTheme();
    const generatedId = useId();
    const inputId = id ?? `${generatedId}-input`;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const invalid = hasContent(error);
    const hasHint = hasContent(hint);

    const describedByIds =
      [describedBy, hasHint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') || undefined;

    const controlCls = [
      styles.control,
      theme === 'meadow' ? styles.meadow : styles.flat,
      invalid ? styles.invalid : null,
      disabled ? styles.disabled : null,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={[styles.field, className].filter(Boolean).join(' ')}>
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
        <div className={controlCls}>
          <input
            ref={ref}
            id={inputId}
            type="text"
            className={styles.input}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-describedby={describedByIds}
            {...rest}
          />
          {rightSlot && <span className={styles.slot}>{rightSlot}</span>}
        </div>
        {hasHint && (
          <div id={hintId} className={styles.hint}>
            {hint}
          </div>
        )}
        <div id={errorId} className={styles.error} aria-live="polite">
          {invalid ? error : null}
        </div>
      </div>
    );
  },
);

WordsHappenTextField.displayName = 'WordsHappenTextField';
